/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Breadcrumb, IBreadcrumbItem } from '@fluentui/react/lib/Breadcrumb';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2, important } from 'src/core/themes/luisStylist';
import { routeTable } from 'src/core/utils/routeTable';
import { doesRouteMatchLocation } from 'src/core/utils/routingUtils';

const messages = defineMessages({
    languageStudio: {
        id: 'customText.customTextBreadcrumb.languageStudio',
        defaultMessage: 'Language Studio'
    },
    myStorage: {
        id: 'customText.customTextBreadcrumb.myStorage',
        defaultMessage: 'My Storage'
    },
    myExtractionProjects: {
        id: 'customText.customTextBreadcrumb.myExtractionProjects',
        defaultMessage: 'Custom Named Entity Recognition'
    },
    myClassificationProjects: {
        id: 'customText.customTextBreadcrumb.myClassificationProjects',
        defaultMessage: 'Custom Text Classification'
    },
    tagData: {
        id: 'customText.customTextBreadcrumb.tagData',
        defaultMessage: '{ projectName } - Data labeling'
    },
    autotagData: {
        id: 'customText.customTextBreadcrumb.autotagData',
        defaultMessage: '{ projectName } - Auto-labeling'
    },
    train: {
        id: 'customText.customTextBreadcrumb.train',
        defaultMessage: '{ projectName } - Training jobs'
    },
    modelDetails: {
        id: 'customText.customTextBreadcrumb.modelDetails',
        defaultMessage: '{ projectName } - Model performance'
    },
    improveModel: {
        id: 'customText.customTextBreadcrumb.reviewTestSet',
        defaultMessage: '{ projectName } - Improve model'
    },
    deploy: {
        id: 'customText.customTextBreadcrumb.deploy',
        defaultMessage: '{ projectName } - Deploying a model'
    },
    test: {
        id: 'customText.customTextBreadcrumb.test',
        defaultMessage: '{ projectName } - Testing deployments'
    },
    projectSettings: {
        id: 'customText.customTextBreadcrumb.projectSettings',
        defaultMessage: '{ projectName } - Project settings'
    },
    breadcrumbsAriaLabel: {
        id: 'customText.customTextBreadcrumb.breadcrumbsAriaLabel',
        defaultMessage: 'Main Breadcrumb'
    },
    moreLinksAriaLabel: {
        id: 'customText.customTextBreadcrumb.moreLinksAriaLabel',
        defaultMessage: 'More Links'
    }
});

const { getClassName } = getStylistV2('CustomTextBreadcrumb');

const itemClassName = getClassName('item', important({ fontSize: '14px', fontWeight: 'normal' }));
const linkItemClassName = getClassName('linkItem', theme =>
    important({ color: theme.linkColor, fontSize: '14px', display: 'inline-block', height: '100%' })
);
const linkIconClassName = getClassName('linkIcon', important({ fontSize: '10px' }));

const useBreadcrumbItems = () => {
    const history = useHistory();
    const { pathname } = useLocation();
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const navigateTo = (path: string) => () => history.push(path);

    const doesMatch = (path: string, isExact: boolean = true) =>
        doesRouteMatchLocation(path, { exact: isExact, locationToMatch: pathname });

    const items: IBreadcrumbItem[] = [
        { text: localize(messages.languageStudio), key: 'languageStudio', onClick: navigateTo(routeTable.languageStudio.root()) }
    ];

    const currentProject = rootStore.currentCustomTextProjectStore;
    const projectName = currentProject?.name;
    const projectKind = currentProject?.projectKind;

    const getModelNameFromPathName = () =>
        pathname.split(routeTable.customText.modelDetailsListPage(currentProject?.id, projectKind))[1]?.split('/')[1] ?? '';

    if (doesMatch(routeTable.customText.myStoragePage())) {
        items.push({ text: localize(messages.myStorage), key: 'myStorage' });
    } else if (doesMatch(routeTable.customText.extractionProjectsPage())) {
        items.push({ text: localize(messages.myExtractionProjects), key: 'myExtractionProjects' });
    } else if (doesMatch(routeTable.customText.classificationProjectsPage())) {
        items.push({ text: localize(messages.myClassificationProjects), key: 'myClassificationProjects' });
    } else if (
        doesMatch(routeTable.customText.extractionProjectsPage(), false) ||
        doesMatch(routeTable.customText.classificationProjectsPage(), false)
    ) {
        const isExtractionProject = doesMatch(routeTable.customText.extractionProjectsPage(), false);
        items.push(
            isExtractionProject
                ? {
                      text: localize(messages.myExtractionProjects),
                      key: 'myExtractionProjects',
                      onClick: navigateTo(routeTable.customText.extractionProjectsPage())
                  }
                : {
                      text: localize(messages.myClassificationProjects),
                      key: 'myClassificationProjects',
                      onClick: navigateTo(routeTable.customText.classificationProjectsPage())
                  }
        );

        if (doesMatch(routeTable.customText.dataTaggingPage(projectName, projectKind))) {
            items.push({ text: localize(messages.tagData, { projectName }), key: 'tagData' });
        } else if (doesMatch(routeTable.customText.autoTaggingPage(projectName, projectKind))) {
            items.push({ text: localize(messages.autotagData, { projectName }), key: 'autotagData' });
        } else if (doesMatch(routeTable.customText.trainingPage(projectName, projectKind))) {
            items.push({ text: localize(messages.train, { projectName }), key: 'train' });
        } else if (doesMatch(routeTable.customText.modelDetailsPage(projectName, projectKind), false)) {
            items.push({
                text: localize(messages.modelDetails, { projectName }),
                key: 'viewModelDetails',
                onClick: navigateTo(routeTable.customText.modelDetailsListPage(currentProject?.id, projectKind))
            });

            if (getModelNameFromPathName()) {
                items.push({ text: getModelNameFromPathName(), key: 'viewModel' });
            }
        } else if (doesMatch(routeTable.customText.modelDetailsListPage(projectName, projectKind), false)) {
            items.push({ text: localize(messages.modelDetails, { projectName }), key: 'viewModelDetails' });
        } else if (doesMatch(routeTable.customText.deployModelPage(projectName, projectKind))) {
            items.push({ text: localize(messages.deploy, { projectName }), key: 'deployModel' });
        } else if (doesMatch(routeTable.customText.testModelsPage(projectName, projectKind))) {
            items.push({ text: localize(messages.test, { projectName }), key: 'testModel' });
        } else if (doesMatch(routeTable.customText.projectSettingsPage(projectName, projectKind))) {
            items.push({ text: localize(messages.projectSettings, { projectName }), key: 'projectSettings' });
        }
    }

    return items;
};

export const CustomTextBreadcrumb = observer(() => {
    const items = useBreadcrumbItems();
    const { localize } = useLocalization();

    return (
        <Breadcrumb
            items={items}
            ariaLabel={localize(messages.breadcrumbsAriaLabel)}
            overflowAriaLabel={localize(messages.moreLinksAriaLabel)}
            styles={{
                root: { margin: 0 },
                item: itemClassName,
                chevron: linkIconClassName,
                itemLink: `${itemClassName} ${linkItemClassName}`
            }}
        />
    );
});
